import { QueryClient, useQueries, useQueryClient } from '@tanstack/react-query';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { courseCollectionQueryKey } from './useCourseCollection';

export const ROOT_COURSES_PATH = '/courses';

export interface CourseLevel {
    path: string;
    title: string;
    description: string;
}

interface CourseLevelData {
    title: string;
    description: string;
}

const ROOT_LEVEL: CourseLevel = { path: ROOT_COURSES_PATH, title: 'All courses', description: '' };

// "/courses/a/children/b/children" -> ["/courses", "/courses/a/children", "/courses/a/children/b/children"]
// Paths alternate "courses / id / children / id / children", so a level ends at
// every even position. Checking the position keeps a course whose id is
// literally "children" from being mistaken for a boundary.
export const getCourseLevelPaths = (pathname: string): string[] => {
    const segments = pathname.split('/').filter(Boolean);
    const paths = [ROOT_COURSES_PATH];
    segments.forEach((segment, index) => {
        if (index > 0 && index % 2 === 0 && segment === 'children') {
            paths.push('/' + segments.slice(0, index + 1).join('/'));
        }
    });
    return paths;
};

// "/courses/a/children/b/children" -> parent list "/courses/a/children", document "b"
const splitLevelPath = (levelPath: string) => {
    const segments = levelPath.split('/').filter(Boolean);
    return {
        parentCollectionPath: '/' + segments.slice(0, -2).join('/'),
        docId: segments[segments.length - 2],
        docPath: segments.slice(0, -1).join('/'),
    };
};

const toLevelData = (data: { name?: string; description?: string }): CourseLevelData => ({
    title: data.name || '',
    description: data.description || '',
});

// When the user clicked through, the parent course list is already cached, so
// the level's name and description are available without a read.
const findInParentCache = (queryClient: QueryClient, levelPath: string): CourseLevelData | undefined => {
    const { parentCollectionPath, docId } = splitLevelPath(levelPath);
    const parentDocs = queryClient.getQueryData<Array<{ id: string; name?: string; description?: string }>>(
        courseCollectionQueryKey(parentCollectionPath)
    );
    const match = parentDocs?.find(parentDoc => parentDoc.id === docId);
    return match ? toLevelData(match) : undefined;
};

const fetchCourseLevel = async (levelPath: string): Promise<CourseLevelData> => {
    const { docPath } = splitLevelPath(levelPath);
    const snapshot = await getDoc(doc(db, docPath));
    if (!snapshot.exists()) {
        console.error('Course level not found for breadcrumbs:', docPath);
        throw new Error(`Course level not found: ${docPath}`);
    }
    return toLevelData(snapshot.data());
};

// Breadcrumb trail for a course URL. Uses cached parent lists when available
// (clicking through) and reads only the missing level documents otherwise
// (refresh, shared nested link, "Go to Course" from My Notes).
// Returns null until every level is known, or if any level can't be loaded.
export const useCourseTrail = (pathname: string): CourseLevel[] | null => {
    const queryClient = useQueryClient();
    const levelPaths = getCourseLevelPaths(pathname).slice(1);

    return useQueries({
        queries: levelPaths.map(levelPath => ({
            queryKey: ['courseLevel', levelPath],
            queryFn: () => fetchCourseLevel(levelPath),
            initialData: () => findInParentCache(queryClient, levelPath),
            initialDataUpdatedAt: () =>
                queryClient.getQueryState(courseCollectionQueryKey(splitLevelPath(levelPath).parentCollectionPath))?.dataUpdatedAt,
        })),
        combine: results => {
            if (results.some(result => !result.data)) return null;
            return [
                ROOT_LEVEL,
                ...results.map((result, index) => ({ path: levelPaths[index], ...result.data! })),
            ];
        },
    });
};
